/**
 * RecentSlashesStrip: slim live strip that shows the most recent real slash
 * event from the on-chain indexer, with a verify-on-BaseScan link and the
 * signed content hash. Fully honest empty state when no slashes exist yet.
 *
 * Backed by: GET /api/packets?limit=1
 * Refreshes every 60s (indexer polls the chain every 30s).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ShieldCheck, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 60_000;

function timeAgo(iso) {
  if (!iso) return '';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function shortHash(h, head = 6, tail = 4) {
  if (!h) return '';
  return h.length > head + tail + 3 ? `${h.slice(0, head)}…${h.slice(-tail)}` : h;
}

export default function RecentSlashesStrip({ variant = 'default' }) {
  const [packet, setPacket] = useState(null);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState('loading'); // loading | ok | error | empty

  useEffect(() => {
    let canceled = false;
    const fetchNow = async () => {
      try {
        const r = await fetch(`${API}/api/packets?limit=1`, { cache: 'no-store' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (canceled) return;
        setTotal(d.total || 0);
        if (d.packets && d.packets.length > 0) {
          setPacket(d.packets[0]);
          setState('ok');
        } else {
          setPacket(null);
          setState('empty');
        }
      } catch (e) {
        if (!canceled) setState('error');
      }
    };
    fetchNow();
    const t = setInterval(fetchNow, REFRESH_MS);
    return () => {
      canceled = true;
      clearInterval(t);
    };
  }, []);

  const compact = variant === 'compact';

  if (state === 'loading') {
    return (
      <div className={`w-full ${compact ? 'py-2' : 'py-3'} px-4 border-y border-white/5 bg-white/[0.015] animate-pulse`} data-testid="recent-slashes-strip-loading">
        <div className="container mx-auto max-w-6xl text-xs text-white/30">Reading the public breach record…</div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className={`w-full ${compact ? 'py-2' : 'py-3'} px-4 border-y border-rose-500/10 bg-rose-500/[0.03]`} data-testid="recent-slashes-strip-error">
        <div className="container mx-auto max-w-6xl text-xs text-rose-300">
          Breach record unavailable. Try <Link to="/proofs" className="underline hover:text-rose-200">/proofs</Link>.
        </div>
      </div>
    );
  }

  if (state === 'empty' || !packet) {
    return (
      <div className={`w-full ${compact ? 'py-2.5' : 'py-3'} px-4 border-y border-white/[0.06] bg-white/[0.015]`} data-testid="recent-slashes-strip-empty">
        <div className="container mx-auto max-w-6xl flex items-center gap-3 flex-wrap text-xs">
          <span className="inline-flex items-center gap-1.5 text-white/50">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
            BREACH RECORDS
          </span>
          <span className="text-white/60">
            No breach records yet, on mainnet or testnet.
          </span>
          <Link to="/proofs" className="ml-auto text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1" data-testid="recent-slashes-strip-empty-link">
            View ledger <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    );
  }

  const isTestnet = packet.is_testnet;
  const tone = isTestnet ? 'cyan' : 'emerald';
  const toneClass = {
    cyan: { bg: 'bg-cyan-500/[0.04]', border: 'border-cyan-500/15', dot: 'bg-cyan-400', text: 'text-cyan-300' },
    emerald: { bg: 'bg-emerald-500/[0.04]', border: 'border-emerald-500/15', dot: 'bg-emerald-400', text: 'text-emerald-300' },
  }[tone];

  return (
    <div
      className={`w-full ${compact ? 'py-2.5' : 'py-3'} px-4 border-y ${toneClass.border} ${toneClass.bg}`}
      data-testid="recent-slashes-strip"
    >
      <div className="container mx-auto max-w-6xl flex items-center gap-x-5 gap-y-2 flex-wrap text-xs">
        <span className={`inline-flex items-center gap-2 font-semibold ${toneClass.text} whitespace-nowrap`}>
          <span className={`w-1.5 h-1.5 rounded-full ${toneClass.dot} animate-pulse`} />
          {isTestnet ? 'Last breach record: Base Sepolia test' : 'Last breach record: Base mainnet'}
        </span>

        <span className="text-white/60 hidden sm:inline">
          Agent #{packet.agent?.agent_id}
        </span>
        {packet.collateral_token?.is_testnet_token && (
          <>
            <span className="text-white/40 hidden md:inline">·</span>
            <span className="text-amber-300/80 hidden md:inline text-[10px] uppercase tracking-wider font-semibold">
              test token
            </span>
          </>
        )}
        <span className="text-white/40 hidden md:inline">·</span>
        <span className="text-white/50 hidden md:inline inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {timeAgo(packet.produced_at)}
        </span>

        <a
          href={packet.slash?.tx_explorer_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/70 hover:text-white inline-flex items-center gap-1 font-mono"
          data-testid="recent-slashes-strip-tx"
          title={packet.slash?.tx_hash}
        >
          {shortHash(packet.slash?.tx_hash, 8, 6)}
          <ExternalLink className="w-3 h-3" />
        </a>

        <span
          className="hidden lg:inline-flex items-center gap-1 text-white/40 font-mono"
          title={`SHA-256: ${packet.signature?.content_hash_sha256}`}
          data-testid="recent-slashes-strip-hash"
        >
          <ShieldCheck className="w-3 h-3" />
          {shortHash(packet.signature?.content_hash_sha256, 6, 4)}
        </span>

        <Link
          to="/proofs"
          className="ml-auto text-white/70 hover:text-white inline-flex items-center gap-1 font-medium whitespace-nowrap"
          data-testid="recent-slashes-strip-more"
        >
          {total > 1 ? `+${total - 1} more` : 'View proof'} <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
